/**
 * Supabase Client for Space Operator Copilot Frontend
 *
 * Initializes the Supabase client for authentication and database operations.
 * Uses environment variables for configuration.
 *
 * In E2E test mode (vite --mode test), a stub client is used so the AlphaGate
 * login UI renders without making real network requests.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables in production
if (!supabaseUrl || !supabaseAnonKey) {
  if (import.meta.env.PROD) {
    throw new Error(
      'Missing Supabase environment variables. ' +
        'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment.'
    );
  }
  // In development without Supabase configured, provide a warning
  console.warn(
    'Supabase environment variables not configured. ' +
      'Authentication features will not work until configured.'
  );
}

/**
 * Stub Supabase client for E2E testing (vite --mode test).
 * Satisfies the SupabaseClient interface used by AlphaGate without making
 * network requests, so Playwright's networkidle resolves immediately.
 */
const createStubClient = (): SupabaseClient => {
  const noop = () => {};
  return {
    auth: {
      getSession: () =>
        Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({
        data: { subscription: { unsubscribe: noop } },
      }),
      signInWithOtp: () =>
        new Promise((resolve) =>
          setTimeout(
            () => resolve({ data: { user: null, session: null }, error: null }),
            500
          )
        ),
      signOut: () => Promise.resolve({ error: null }),
    },
    functions: {
      invoke: () =>
        new Promise((resolve) =>
          setTimeout(() => resolve({ data: null, error: null }), 500)
        ),
    },
  } as unknown as SupabaseClient;
};

const isE2ETestMode = import.meta.env.MODE === 'test';

/**
 * Supabase client instance.
 * May be null if environment variables are not configured (dev mode only).
 * In E2E test mode, a stub client is used to render the login UI.
 */
export const supabase: SupabaseClient | null = isE2ETestMode
  ? createStubClient()
  : supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
          storageKey: 'space-operator-auth',
        },
      })
    : null;

/**
 * Check if Supabase client is configured and available.
 */
export const isSupabaseConfigured = (): boolean => {
  return supabase !== null;
};

/**
 * Get the Supabase client, throwing an error if not configured.
 * Use this when Supabase is required.
 */
export const getSupabaseClient = (): SupabaseClient => {
  if (!supabase) {
    throw new Error(
      'Supabase client is not configured. ' +
        'Ensure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.'
    );
  }
  return supabase;
};

export type AuthError = {
  message: string;
  status?: number;
};
